import React, {Component, ReactNode} from 'react'
import {createResidentPage, ResidentPagePropsSet} from '../shared/hoc/createResidentPage'
import ReservationsRepository from './ReservationsRepository'
import EventEmitterExResident from '../shared/pageBaseComponents/EventEmitterExResident'
import FacilityForReservation from '../facilities/FacilityForReservation'
import {FacilityReservation} from './ReserveFacilityModel'
import moment from 'moment'
import {convertToNullableMoment} from './helpers/helpers'
import {Nullable} from '../../app/TypeHelpers'
import {
    CenterPlainLinkStyledButton,
    PlainLinkStyledBackButton,
    PrimaryStyledButton,
} from '../../styledComponents/Button'
import {NatAsyncCallFunction} from '../../__NoAutomatedTests/common/util/Natutil'
import {HttpError} from '../../network/errors/HttpError'
import {Messages} from '../../Messages'
import {RequestError} from '../../network/response/RequestError'
import {ErrorResponseBody} from '../../network/response/ErrorResponseBody'
import {BlockSpacer} from '../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../styledComponents/DisplayItem'
import {StyledPageTitle} from './components/CommonStyle'
import ReservationDetailItem from './components/ReservationDetailItem'
import styled from 'styled-components'
import {Colors} from '../../styledComponents/Constants'
import {
    setResidentMyReservationsPageSearchDate,
} from './ResidentMyReservationsComponents/RMRReservationStatusesComponent'
import {
    clearResidentReserveTopPageSearchConditions,
} from './ResidentReserveFacilityTopComponents/RRFTFacilitySearchComponent'
import {
    NoticeMessageBody,
    NoticeMessageHeader,
    ReservationTypeTagForFirstCome,
    ReservationTypeTagForLottery,
} from './ResidentReserveFacilityInputConfirmPage'
import {RESERVATION_TYPE} from '../../CodeMaster'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    facilityId: number
    reserveId: number
    reservationsRepo: ReservationsRepository
    eventEmitter: EventEmitterExResident
    location: Location<LocationState>
}

interface LocationState {
    previousPageUrl: string,
    facilityInfo: FacilityForReservation
    reserveInfo: FacilityReservation
}

class ResidentReserveFacilityCancelConfirmPage extends Component<Props & ResidentPagePropsSet> {

    constructor(props: Readonly<Props & ResidentPagePropsSet>) {
        super(props)

        this.handleCancelButtonClick = this.handleCancelButtonClick.bind(this)
        this.handleBackLinkOnClick = this.handleBackLinkOnClick.bind(this)
    }

    async handleBackLinkOnClick(): Promise<void> {
        const {navigate, condominiumCd, facilityId, reserveId} = this.props
        const locationState: LocationState = this.props.location.state

        navigate(`/resident/condominiums/${condominiumCd}/facilities/${facilityId}/reservations/${reserveId}/detail`, {
            replace: true,
            state: {
                previousPageUrl: locationState.previousPageUrl,
            },
        })
    }

    async handleCancelButtonClick(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        const locationState: LocationState = this.props.location.state
        const {condominiumCd, facilityId, reserveId, reservationsRepo, navigate} = this.props
        const {reserveInfo, previousPageUrl} = this.props.location.state

        try {
            const returnValue = await NatAsyncCallFunction(reservationsRepo.cancelFacilityReservation.bind(
                reservationsRepo,
                condominiumCd,
                facilityId,
                reserveId,
                reserveInfo,
            ), navigate)
            if (returnValue === null) {
                return
            }

            setResidentMyReservationsPageSearchDate(locationState.reserveInfo.startDate)
            clearResidentReserveTopPageSearchConditions()

            navigate(
                `/resident/condominiums/${condominiumCd}/facilities/${facilityId}/reservations/${reserveId}/cancelComplete`,
                {replace: true},
            )

        } catch (e) {
            const httpError: HttpError = e as HttpError
            const {requestError, requestItemErrors} = httpError.responseBody
            const cancelErrorResponseBody = new ErrorResponseBody(
                new RequestError(
                    requestError.messageId,
                    Messages.EFACC0018.toString(),
                    requestError.message,
                    requestError.type,
                ),
                requestItemErrors,
            )

            navigate(
                `/resident/condominiums/${condominiumCd}/facilities/${facilityId}/reservations/${reserveId}/detail`,
                {
                    replace: true,
                    state: {
                        previousPageUrl: previousPageUrl,
                        errorResponseBody: cancelErrorResponseBody,
                    },
                },
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    render(): ReactNode {
        const {facilityInfo, reserveInfo} = this.props.location.state
        const {overnightFacilitySetting} = facilityInfo
        const {startDate, startTime, endDate, endTime, stayCount, reservationTitle, reserveType} = reserveInfo

        if (reserveType !== RESERVATION_TYPE.FIRST_COME.value &&
            reserveType !== RESERVATION_TYPE.LOTTERY.value) {
            return null
        }

        let startDateTimeString: string
        let endDateTimeString: string
        if (facilityInfo.stayAvailability) {
            if (overnightFacilitySetting === null) {
                throw Error('OvernightFacilitySetting is null')
            }
            if (stayCount === null) {
                throw Error('StayCount is null')
            }
            const startDateTimeMoment: Nullable<moment.Moment> =
                convertToNullableMoment(startDate, overnightFacilitySetting.checkinTime)
            const endDateMoment: Nullable<moment.Moment> =
                convertToNullableMoment(startDate, overnightFacilitySetting.checkoutTime)
            if (startDateTimeMoment === null) {
                throw Error('StartDate is null')
            }
            if (endDateMoment === null) {
                throw Error('EndDate is null')
            }
            const endDateTimeMoment = endDateMoment.clone().add(stayCount, 'day')
            startDateTimeString = startDateTimeMoment.format('YYYY年M月D日(ddd) H:mm')
            endDateTimeString = endDateTimeMoment.format('YYYY年M月D日(ddd) H:mm')
        } else {
            const startDateTimeMoment: Nullable<moment.Moment> =
                convertToNullableMoment(startDate, startTime)
            const endDateTimeMoment: Nullable<moment.Moment> =
                convertToNullableMoment(endDate, endTime)
            if (startDateTimeMoment === null) {
                throw Error('StartDate is null')
            }
            if (endDateTimeMoment === null) {
                throw Error('EndDate is null')
            }
            startDateTimeString = startDateTimeMoment.format('YYYY年M月D日(ddd) H:mm')
            endDateTimeString = endDateTimeMoment.format('YYYY年M月D日(ddd) H:mm')
        }

        let reservationTypeTag: ReactNode
        if (RESERVATION_TYPE.FIRST_COME.value === reserveType) {
            reservationTypeTag = <ReservationTypeTagForFirstCome>
                {RESERVATION_TYPE.FIRST_COME.label}
            </ReservationTypeTagForFirstCome>
        } else {    // RESERVATION_TYPE.LOTTERY.value === reserveType
            reservationTypeTag = <ReservationTypeTagForLottery>
                {RESERVATION_TYPE.LOTTERY.label}
            </ReservationTypeTagForLottery>
        }

        return <>
            <PlainLinkStyledBackButton onClick={this.handleBackLinkOnClick}>戻る</PlainLinkStyledBackButton>
            <BlockSpacer height={'15px'}/>
            <DisplayItemDividerForPageTop/>
            <BlockSpacer height={'30px'}/>
            <StyledPageTitle>キャンセル確認</StyledPageTitle>
            <NoticeMessageHeader>
                予約のキャンセルは完了していません。
            </NoticeMessageHeader>
            <NoticeMessageBody>
                予約内容をご確認のうえ、「予約をキャンセルする」ボタンを押してください。<br/>
                ボタンを押すと予約をキャンセルします。
            </NoticeMessageBody>
            <BlockSpacer height={'20px'}/>
            {reservationTypeTag}
            <BlockSpacer height={'10px'}/>
            <ReservationDetailItem
                type={'texts'}
                title={'施設名'}
                texts={[reserveInfo.facilityName]}
            />

            <BlockSpacer height={'5px'}/>
            <ReservationDetailItem
                type={'texts'}
                title={'利用日時'}
                texts={[
                    `${startDateTimeString}から`,
                    `${endDateTimeString}まで`,
                ]}
            />

            <div>
                {
                    (reservationTitle && reservationTitle !== '') &&
                    <>
                        <BlockSpacer height={'5px'}/>
                        <ReservationDetailItem
                            type={'texts'} title={'予約タイトル'}
                            texts={[reservationTitle]}
                        />
                    </>
                }
                <BlockSpacer height={'30px'}/>
                <CancelButton onClick={this.handleCancelButtonClick}>
                    予約をキャンセルする
                </CancelButton>
            </div>
            <BlockSpacer height={'30px'}/>
            <CenterPlainLinkStyledButton onClick={this.handleBackLinkOnClick}>
                予約詳細へ戻る
            </CenterPlainLinkStyledButton>
            <BlockSpacer height={'15px'}/>
        </>
    }
}

export default createResidentPage(ResidentReserveFacilityCancelConfirmPage)

const CancelButton = styled(PrimaryStyledButton)`
  border: 1px solid ${Colors.Alert};
  box-shadow: 0 2px 0 0 ${Colors.AlertVariant};
  background-color: ${Colors.Alert};
  color: ${Colors.Surface};

  :active {
    background-color: ${Colors.AlertActive};
    box-shadow: none;
  }
`
